import React, {createContext, useContext, useEffect, useState} from "react";
import {Snackbar} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import {makeStyles} from "@material-ui/core/styles";
import clsx from "clsx";

const useStyles = makeStyles(theme => ({
    /*
     * fixed, e nao sticky: as pilhas abaixo tem height: 100vh e, com o container
     * no fluxo, a primeira delas somava 100vh a partir do topo do <main> — ou
     * seja, altura do Header a mais no documento, gerando barra de rolagem
     * vertical e uma faixa vazia no fim de toda pagina. Fora do fluxo, o
     * container nao empurra mais nada e as pilhas passam a se ancorar no topo
     * da janela, que e onde o toast deve aparecer.
     */
    container: {
        zIndex: 9999999,
        top: 0,
        left: 0,
        position: 'fixed',
        pointerEvents: 'none',
        height: 0,
        width: '100%',
    },
    stack: {
        padding: '16px 32px 16px 22px',
        gap: theme.spacing(1),
        top: '-100%',
        left: 0,
        position: 'relative',
        pointerEvents: 'none',
        height: '100vh',
        width: '100%',
    },
    topLeftStack: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
    },
    topCenterStack: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        marginTop: 'calc(-100vh)',
    },
    topRightStack: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        marginTop: 'calc(-100vh)',
    },
    bottomLeftStack: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        marginTop: 'calc(-100vh)',
    },
    bottomCenterStack: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: 'calc(-100vh)',
    },
    bottomRightStack: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        marginTop: 'calc(-100vh)',
    },
    snackbar: {
        pointerEvents: 'auto',
        position: 'relative',
        left: 0,
        right: 0,
        bottom: 0,
        top: 0,
        transform: 'none',
    },
    alertSuccess: {
        borderRadius: '0 4px 4px 0 !important',
        filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25))',
        backdropFilter: 'blur(5px)',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: -8,
            width: '8px',
            height: '100%',
            borderRadius: '4px 0 0 4px',
            background: 'rgb(76, 175, 80)',
            opacity: 1 + '!important',
            zIndex: -1,
        },
    },
    alertInfo: {
        borderRadius: '0 4px 4px 0 !important',
        filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25))',
        backdropFilter: 'blur(5px)',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: -8,
            width: '8px',
            height: '100%',
            borderRadius: '4px 0 0 4px',
            background: 'rgb(33, 150, 243)',
            opacity: 1 + '!important',
            zIndex: -1,
        },
    },
    alertError: {
        borderRadius: '0 4px 4px 0 !important',
        filter: 'drop-shadow(0px 0px 4px rgba(0, 0, 0, 0.25))',
        backdropFilter: 'blur(5px)',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: -8,
            width: '8px',
            height: '100%',
            borderRadius: '4px 0 0 4px',
            background: 'rgb(244, 67, 54)',
            opacity: 1 + '!important',
            zIndex: -1,
        },
    },
}));

type TSeverity = 'success' | 'info' | 'error';

type TAnchorOrigin = {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
}

interface IToast {
    open?: boolean;
    severity: TSeverity;
    message: string;
    anchorOrigin?: TAnchorOrigin;
    key?: number;
}

interface IToastProviderProps {
    children: React.ReactNode;
}

export const ToastContext = createContext({
    createToast: (toast: IToast) => {
    },
});

const ToastProvider = (props: IToastProviderProps) => {
    const {
        children
    } = props;

    const classes = useStyles();

    const [toasts, setToasts] = useState<IToast[]>([]);

    const createToast = (toast: IToast) => {
        toast.open = true;

        if (!toast.anchorOrigin) {
            toast.anchorOrigin = {
                vertical: 'top',
                horizontal: 'right',
            }
        }

        const newToast = {...toast, key: Date.now()};
        setToasts((prev) => [...prev, newToast]);

        setTimeout(() => {
            removeToast(newToast);
        }, 5000);
    }

    const removeToast = (toast: IToast) => {
        setToasts((prevToasts) =>
            prevToasts.map((_toast) =>
                _toast.key === toast.key ? {..._toast, open: false} : toast
            )
        );

        setTimeout(() => {
            setToasts((prevToasts) =>
                prevToasts.filter((t) => t.key !== toast.key)
            );
        }, 500);
    };

    const resolveAlertClassNameBySeverity = (severity: TSeverity) => {
        switch (severity) {
            case 'success':
                return classes.alertSuccess;
            case 'info':
                return classes.alertInfo;
            case 'error':
                return classes.alertError;
            default:
                return classes.alertInfo;
        }
    }

    const getToastsByAnchorOrigin = (anchorOrigin: TAnchorOrigin) => {
        return toasts.filter((toast) =>
            toast.anchorOrigin?.vertical === anchorOrigin.vertical
            && toast.anchorOrigin?.horizontal === anchorOrigin.horizontal);
    }

    const Toast = (toast: IToast) => (
        <Snackbar
            key={toast.key}
            className={clsx(classes.snackbar)}
            open={toast.open}
            anchorOrigin={toast.anchorOrigin}>
            <Alert
                className={clsx(resolveAlertClassNameBySeverity(toast.severity))}
                severity={toast.severity}
                onClose={() => removeToast(toast)}>
                {toast.message}
            </Alert>
        </Snackbar>
    )

    return (
        <ToastContext.Provider value={{createToast}}>
            <div className={clsx(classes.container)}>
                <div className={clsx(classes.stack, classes.topLeftStack)}>
                    {getToastsByAnchorOrigin({vertical: 'top', horizontal: 'left'}).map((toast) => (
                        Toast(toast)
                    ))}
                </div>
                <div className={clsx(classes.stack, classes.topCenterStack)}>
                    {getToastsByAnchorOrigin({vertical: 'top', horizontal: 'center'}).map((toast) => (
                        Toast(toast)
                    ))}
                </div>
                <div className={clsx(classes.stack, classes.topRightStack)}>
                    {getToastsByAnchorOrigin({vertical: 'top', horizontal: 'right'}).map((toast) => {
                        console.log(toast);
                        return Toast(toast);
                    })}
                        </div>
                        <div className={clsx(classes.stack,classes.bottomRightStack)}>
                    {getToastsByAnchorOrigin({vertical: 'bottom', horizontal: 'right'}).map((toast) => (
                        Toast(toast)
                    ))}
                </div>
                <div className={clsx(classes.stack, classes.bottomCenterStack)}>
                    {getToastsByAnchorOrigin({vertical: 'bottom', horizontal: 'center'}).map((toast) => (
                        Toast(toast)
                    ))}
                </div>
                <div className={clsx(classes.stack, classes.bottomLeftStack)}>
                    {getToastsByAnchorOrigin({vertical: 'bottom', horizontal: 'left'}).map((toast) => (
                        Toast(toast)
                    ))}
                </div>
            </div>
            {children}
        </ToastContext.Provider>
    )
}

const useToast = () => {
    const {createToast} = useContext(ToastContext);
    return {createToast};
}

export {ToastProvider, useToast}